import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { UsageBadge } from "@/components/UsageBadge";
import { TABS } from "@/components/booking-form/registry/tabs";
import { RotateCcw, EyeOff, Eye, Plus, Trash2 } from "lucide-react";

const VisibleLabel = ({ visible }) => (
  <span className="text-xs text-slate-500 inline-flex items-center gap-1">
    {visible ? <><Eye className="h-3 w-3" /> Visible</> : <><EyeOff className="h-3 w-3" /> Hidden</>}
  </span>
);

const TabRow = ({ fd, tab }) => {
  const dirty = fd.isDirty("tab", tab.key);
  const label = fd.effective("tab", tab.key, "label", tab.label);
  const visible = fd.effective("tab", tab.key, "is_visible", true);
  const idle = tab.isCustom ? "border-violet-200 bg-violet-50/30" : "border-transparent";
  return (
    <div className={`grid grid-cols-1 sm:grid-cols-12 gap-2 items-center p-2 rounded border ${dirty ? "border-amber-300 bg-amber-50/40" : idle}`}
         data-testid={`fd-tab-row-${tab.key}`}>
      <div className="sm:col-span-3">
        <code className="text-xs text-slate-500">{tab.key}</code>
        {tab.isCustom
          ? <div className="text-xs text-violet-600 font-medium">custom tab</div>
          : <div className="text-xs text-slate-400">default: {tab.label}</div>}
      </div>
      <div className="sm:col-span-5">
        <Input value={label}
               onChange={(e) => fd.setDraftValue("tab", tab.key, "label", e.target.value)}
               placeholder={tab.isCustom ? undefined : tab.label}
               data-testid={`fd-tab-label-${tab.key}`} />
      </div>
      <div className="sm:col-span-3 flex items-center gap-2">
        <Switch checked={visible}
                onCheckedChange={(v) => fd.setDraftValue("tab", tab.key, "is_visible", v)}
                data-testid={`fd-tab-visible-${tab.key}`} />
        <VisibleLabel visible={visible} />
        {!tab.isCustom && <UsageBadge scope="tab" itemKey={tab.key} />}
      </div>
      <div className="sm:col-span-1 text-right">
        {tab.isCustom ? (
          <Button size="sm" variant="ghost" onClick={() => fd.deleteCustomTab(tab)}
                  data-testid={`fd-tab-delete-${tab.key}`} title="Delete custom tab">
            <Trash2 className="h-3 w-3 text-rose-600" />
          </Button>
        ) : (
          <Button size="sm" variant="ghost" onClick={() => fd.resetRow("tab", tab.key)}
                  disabled={!fd.savedFor("tab", tab.key) && !dirty}
                  data-testid={`fd-tab-reset-${tab.key}`}>
            <RotateCcw className="h-3 w-3" />
          </Button>
        )}
      </div>
    </div>
  );
};

export const TabsCard = ({ fd }) => (
  <Card>
    <CardHeader className="flex flex-row items-center justify-between">
      <CardTitle className="text-base">Tabs</CardTitle>
      <Button size="sm" variant="outline" onClick={fd.addCustomTab} disabled={!fd.clientId}
              data-testid="fd-add-tab-btn">
        <Plus className="h-3.5 w-3.5 mr-1" /> Add tab
      </Button>
    </CardHeader>
    <CardContent className="space-y-2">
      {[...TABS, ...fd.customTabs].map((t) => <TabRow key={t.key} fd={fd} tab={t} />)}
      {fd.customTabs.length === 0 && (
        <p className="text-xs text-slate-400 italic px-2" data-testid="fd-no-custom-tabs">
          No custom tabs yet. Use &quot;Add tab&quot; to create tenant-specific tabs (e.g. &quot;Compliance Docs&quot;, &quot;Hazmat&quot;).
        </p>
      )}
    </CardContent>
  </Card>
);
